import { Header } from '@features/user-account/components/Header';
import { StyledIconButton } from '@features/user-account/components/StyledIconButton';
import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { LinkSide, Organization, OrganizationLink, organizationLinkColumns } from '@protos/organization';
import { toastifyService } from '@services/ToastifyService';
import { CellRendererActionButton } from '@shared/components/CellRendererActionButton';
import theme from '@shared/themes/darkTheme';
import { apiErrorMessage } from '@utils/apiError';
import { filterColumnChooserFromMainMenu } from '@utils/contextMenuUtils';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { EditOrganizationLinkDialog } from './EditOrganizationLinkDialog';

type Props = {
  organization: Organization;
  canEdit: boolean;
  // Trade limit scope is derived from these links, so a sibling has to be told they moved
  onLinksChanged: () => void;
};

type LinkListProps = {
  title: string;
  helperText: string;
  emptyText: string;
  side: LinkSide;
  links: OrganizationLink[];
  error?: Error;
  loading: boolean;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (link: OrganizationLink) => void;
  onDelete: (link: OrganizationLink) => void;
};

const LinkList: React.FC<LinkListProps> = ({ title, helperText, emptyText, side, links, error, loading, canEdit, onAdd, onEdit, onDelete }) => {
  const columnDefs = useMemo(() => {
    const columns = organizationLinkColumns(side);
    if (!canEdit) return columns;
    const actionsColumn: ColDef<OrganizationLink> = {
      headerName: 'Actions',
      pinned: 'right',
      flex: 2,
      cellRenderer: (params: ICellRendererParams<OrganizationLink>) =>
        params.data ? (
          <Box display="flex" flexDirection="row" gap={1} alignItems="center" justifyContent="center">
            <CellRendererActionButton
              label="Edit"
              onClick={() => onEdit(params.data as OrganizationLink)}
              backgroundColor="white"
              textColor={theme.palette.background.default}
            />
            <CellRendererActionButton
              label="Delete"
              onClick={() => onDelete(params.data as OrganizationLink)}
              backgroundColor={theme.palette.error.dark}
            />
          </Box>
        ) : null,
    };
    return [...columns, actionsColumn];
  }, [side, canEdit, onEdit, onDelete]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Header header={title} helperText={helperText} />
        {/* Adding needs the current links to tell a new one from an overwrite, so it
            waits for them: the upsert key is (agent, client, group) */}
        {canEdit && (
          <StyledIconButton onClick={onAdd} disabled={loading || !!error}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        )}
      </Stack>
      {/* Both sides are optional, so an empty one is a note rather than an empty grid
          demanding to be filled in. A failed read is not an absence of links though, and
          emptyText asserts one, which matters because the limit scope derives from these. */}
      {error ? (
        <Typography fontSize={11} color="error" sx={{ marginTop: '6px' }}>
          Could not load these links: {apiErrorMessage(error, 'the request failed')}. This is not the same as having none.
        </Typography>
      ) : loading ? (
        <Typography fontSize={11} sx={{ marginTop: '6px' }}>
          Loading links...
        </Typography>
      ) : links.length ? (
        <Box height="220px">
          <AgGridReact
            containerStyle={{ height: '100%', width: '100%', marginTop: '10px' }}
            className="ag-theme-balham-dark custom-grid"
            columnDefs={columnDefs}
            rowData={links}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            getMainMenuItems={filterColumnChooserFromMainMenu}
            defaultColDef={{ suppressHeaderMenuButton: true }}
          />
        </Box>
      ) : (
        <Typography fontSize={11} sx={{ marginTop: '6px' }}>
          {emptyText}
        </Typography>
      )}
    </Box>
  );
};

export const OrganizationLinks: React.FC<Props> = ({ organization, canEdit, onLinksChanged }) => {
  const { apiClient } = useAdminApi();
  const [reloadKey, setReloadKey] = useState(0);
  const [dialogSide, setDialogSide] = useState<LinkSide | undefined>(undefined);
  const [linkToEdit, setLinkToEdit] = useState<OrganizationLink | undefined>(undefined);
  const [linkToDelete, setLinkToDelete] = useState<OrganizationLink | undefined>(undefined);

  const reload = useCallback(() => {
    setReloadKey(key => key + 1);
    onLinksChanged();
  }, [onLinksChanged]);

  // Two directions, two calls: an organization can be the agent for one group and the
  // client for another, so showing only one side would hide the other relationship.
  const asAgent = useAsync(async () => {
    const links = await apiClient?.getOrganizationLinks({ agent_organization_id: organization.id });
    return links ?? [];
  }, [apiClient, organization.id, reloadKey]);

  const asClient = useAsync(async () => {
    const links = await apiClient?.getOrganizationLinks({ client_organization_id: organization.id });
    return links ?? [];
  }, [apiClient, organization.id, reloadKey]);

  // Stable handlers: LinkList builds its column defs from these, and a new function
  // each render would make the grid rebuild its columns and lose their state
  const onAddAgent = useCallback(() => {
    setLinkToEdit(undefined);
    setDialogSide('agent');
  }, []);

  const onAddClient = useCallback(() => {
    setLinkToEdit(undefined);
    setDialogSide('client');
  }, []);

  const onEditAgent = useCallback((link: OrganizationLink) => {
    setLinkToEdit(link);
    setDialogSide('agent');
  }, []);

  const onEditClient = useCallback((link: OrganizationLink) => {
    setLinkToEdit(link);
    setDialogSide('client');
  }, []);

  const onDelete = useCallback((link: OrganizationLink) => setLinkToDelete(link), []);

  const confirmDelete = useCallback(async () => {
    if (!apiClient || !linkToDelete) return;
    setLinkToDelete(undefined);
    try {
      await apiClient.deleteOrganizationLink(linkToDelete.id);
      toastifyService.showSuccess('Organization link deleted');
      reload();
    } catch (e) {
      toastifyService.showErrorMessage(apiErrorMessage(e, 'Could not delete the organization link'));
    }
  }, [apiClient, linkToDelete, reload]);

  return (
    <>
      <LinkList
        title="Clients of this agent"
        helperText="Links where this organization is the agent. It administers these clients within the scope shown"
        emptyText="No clients. This organization administers nobody, so AGENT_ADMIN on its users grants nothing yet."
        side="agent"
        links={asAgent.value ?? []}
        error={asAgent.error}
        loading={asAgent.loading}
        canEdit={canEdit}
        onAdd={onAddAgent}
        onEdit={onEditAgent}
        onDelete={onDelete}
      />
      <Divider sx={{ margin: '20px 0' }} />
      <LinkList
        title="Agents over this organization"
        helperText="Links where this organization is the client. These agents administer it within the scope shown"
        emptyText="Optional. Nobody administers this organization. Note a principal such as Onyx is never a link: it is recorded as the PRINCIPAL type on its own organization."
        side="client"
        links={asClient.value ?? []}
        error={asClient.error}
        loading={asClient.loading}
        canEdit={canEdit}
        onAdd={onAddClient}
        onEdit={onEditClient}
        onDelete={onDelete}
      />
      {dialogSide && (
        <EditOrganizationLinkDialog
          key={`link-dialog-${dialogSide}-${linkToEdit?.id ?? 'new'}`}
          organization={organization}
          side={dialogSide}
          link={linkToEdit}
          links={(dialogSide === 'agent' ? asAgent.value : asClient.value) ?? []}
          isOpen={!!dialogSide}
          onClose={() => setDialogSide(undefined)}
          onLinkSaved={reload}
        />
      )}
      <Dialog open={!!linkToDelete} onClose={() => setLinkToDelete(undefined)}>
        <DialogTitle>Delete organization link</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography fontSize={12}>
            {linkToDelete?.agent_organization_name ?? `#${linkToDelete?.agent_organization_id}`} loses authority over{' '}
            {linkToDelete?.client_organization_name ?? `#${linkToDelete?.client_organization_id}`} on {linkToDelete?.scope_product_group} straight
            away. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkToDelete(undefined)} size="small" variant="outlined" style={{ fontSize: 11 }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} size="small" variant="contained" color="error" style={{ fontSize: 11 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
